<template>
  <div class="gym-page" id="top">

    <GymNavBar />

    <main>

    <!-- ══════════════════════════════════════════
         PAGE HERO
    ══════════════════════════════════════════ -->
    <section class="hiw-hero">
      <div class="hiw-hero-inner">
        <div class="label-chip">Πώς Λειτουργεί</div>
        <h1>Δύο εργαλεία.<br /><span class="accent">Μία πλατφόρμα.</span></h1>
        <p class="muted">
          Το Cloudtec Gym αποτελείται από δύο ολοκληρωμένα περιβάλλοντα:
          το <strong>Admin Panel</strong> για τον διαχειριστή και
          το <strong>Member App</strong> για τα μέλη σου —
          σχεδιασμένα να λειτουργούν μαζί.
        </p>
        <div class="hiw-pills">
          <a href="#admin" class="hiw-pill">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>
            Admin Panel
          </a>
          <a href="#member-app" class="hiw-pill hiw-pill--outline">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17 2H7C5.9 2 5 2.9 5 4v16c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-5 18c-.83 0-1.5-.67-1.5-1.5S11.17 17 12 17s1.5.67 1.5 1.5S12.83 20 12 20zm5-4H7V4h10v12z"/></svg>
            Member App
          </a>
        </div>
      </div>
    </section>

    <!-- ══════════════════════════════════════════
         ADMIN PANEL
    ══════════════════════════════════════════ -->
    <section id="admin" class="panel-section">
      <div class="container panel-hdr">
        <div class="panel-tag">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>
          Admin Panel
        </div>
        <h2>Το κέντρο ελέγχου <span class="accent">του γυμναστηρίου σου</span></h2>
        <p class="muted">Web-based panel — πρόσβαση από οποιαδήποτε συσκευή, οποτεδήποτε. Πλήρης διαχείριση χωρίς να χρειαστεί να εγκαταστήσεις τίποτα.</p>
      </div>

      <div
        v-for="(feat, i) in adminFeatures"
        :key="feat.id"
        class="feat-row"
        :class="{ 'feat-row--rev': i % 2 !== 0 }"
      >
        <div class="feat-content">
          <div class="feat-num">{{ String(i + 1).padStart(2, '0') }}</div>
          <h3>{{ feat.title }}</h3>
          <p class="muted">{{ feat.desc }}</p>
          <ul class="feat-points">
            <li v-for="pt in feat.points" :key="pt">
              <svg class="pt-check" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>{{ pt }}
            </li>
          </ul>
        </div>

        <!-- Single image -->
        <div v-if="feat.images.length === 1" class="feat-imgs">
          <div class="screen-wrap" @click="openLightbox(feat.images, 0)">
            <img :src="feat.images[0].src" :alt="feat.images[0].alt" loading="lazy" />
            <div class="zoom-hint">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zm.5-7H9v2H7v1h2v2h1v-2h2V9h-2V7z"/></svg>
            </div>
          </div>
        </div>

        <!-- Slider for multiple images -->
        <div v-else class="img-slider">
          <div class="slider-img-wrap screen-wrap" @click="openLightbox(feat.images, sliderIndexes[feat.id])">
            <transition name="fade" mode="out-in">
              <img
                :key="sliderIndexes[feat.id]"
                :src="feat.images[sliderIndexes[feat.id]].src"
                :alt="feat.images[sliderIndexes[feat.id]].alt"
                loading="lazy"
              />
            </transition>
            <div class="zoom-hint">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zm.5-7H9v2H7v1h2v2h1v-2h2V9h-2V7z"/></svg>
            </div>
          </div>
          <button class="slider-btn slider-btn--prev" @click.stop="prevSlide(feat.id, feat.images.length)" aria-label="Προηγούμενο">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
          </button>
          <button class="slider-btn slider-btn--next" @click.stop="nextSlide(feat.id, feat.images.length)" aria-label="Επόμενο">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
          </button>
          <div class="slider-dots">
            <button
              v-for="(img, di) in feat.images"
              :key="di"
              :class="['dot', { 'dot--active': sliderIndexes[feat.id] === di }]"
              @click.stop="sliderIndexes[feat.id] = di"
              :aria-label="`Εικόνα ${di + 1}`"
            ></button>
          </div>
          <div class="slider-counter">{{ sliderIndexes[feat.id] + 1 }} / {{ feat.images.length }}</div>
        </div>
      </div>
    </section>

    <!-- ══════════════════════════════════════════
         MEMBER APP
    ══════════════════════════════════════════ -->
    <section id="member-app" class="member-section">
      <div class="container panel-hdr">
        <div class="panel-tag panel-tag--member">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17 2H7C5.9 2 5 2.9 5 4v16c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-5 18c-.83 0-1.5-.67-1.5-1.5S11.17 17 12 17s1.5.67 1.5 1.5S12.83 20 12 20zm5-4H7V4h10v12z"/></svg>
          Member App
        </div>
        <h2>Η εμπειρία <span class="accent">των μελών σου</span></h2>
        <p class="muted">
          Native iOS & Android εφαρμογή διαθέσιμη στα stores. Τα μέλη σου κάνουν κρατήσεις,
          παρακολουθούν την πρόοδό τους και λαμβάνουν push notifications — όλα από το κινητό τους.
        </p>
        <div class="store-badges">
          <a class="store-btn" href="https://play.google.com/store/apps/details?id=com.nvourlidas.ctgym" target="_blank" rel="noopener">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3,20.5V3.5C3,2.91,3.34,2.39,3.84,2.15L13.69,12L3.84,21.85C3.34,21.6,3,21.09,3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08,20.75,11.5,20.75,12C20.75,12.5,20.5,12.92,20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/></svg>
            Google Play
          </a>
          <a class="store-btn" href="https://apps.apple.com/gr/app/cloudtec-gym/id6757802948" target="_blank" rel="noopener">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            App Store
          </a>
        </div>
      </div>

      <div class="member-screens">
        <div
          v-for="(screen, si) in memberScreens"
          :key="screen.title"
          class="member-screen"
          @click="openLightbox(memberScreenImages, si)"
        >
          <div class="phone-frame">
            <img :src="screen.img" :alt="screen.title" loading="lazy" />
            <div class="phone-zoom-hint">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zm.5-7H9v2H7v1h2v2h1v-2h2V9h-2V7z"/></svg>
            </div>
          </div>
          <div class="screen-label">
            <div class="screen-title">{{ screen.title }}</div>
            <div class="screen-desc muted">{{ screen.desc }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ══════════════════════════════════════════
         CTA
    ══════════════════════════════════════════ -->
    <section class="hiw-cta">
      <div class="container">
        <h2>Έτοιμος να ξεκινήσεις;</h2>
        <p class="muted">Χωρίς κάρτα. Ακύρωση οποτεδήποτε.</p>
        <div class="cta-row">
          <a href="https://ctgym.cloudtec.gr" target="_blank" rel="noopener" class="cta-primary">Ξεκινήστε Δωρεάν</a>
          <router-link :to="{ path: '/products/cloudtec-gym', hash: '#pricing' }" class="cta-secondary">Δείτε τα Πλάνα →</router-link>
        </div>
      </div>
    </section>

    </main>

    <!-- ══════════════════════════════════════════
         FOOTER
    ══════════════════════════════════════════ -->
    <footer class="gym-footer">
      <div class="gym-footer-main">

        <div class="gym-footer-brand">
          <div class="gym-footer-logo">
            <img class="gym-logo-img" src="@/assets/CTGYM.YELLOW 1080x1080.svg" alt="Cloudtec Gym logo" />
            <span>Cloudtec <strong>Gym</strong></span>
          </div>
          <p class="gym-footer-tagline">Ολοκληρωμένη πλατφόρμα διαχείρισης γυμναστηρίου για επαγγελματίες.</p>
          <a class="gym-footer-appstore-btn"
             href="https://play.google.com/store/apps/details?id=com.nvourlidas.ctgym"
             target="_blank" rel="noopener">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,20.5V3.5C3,2.91,3.34,2.39,3.84,2.15L13.69,12L3.84,21.85C3.34,21.6,3,21.09,3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08,20.75,11.5,20.75,12C20.75,12.5,20.5,12.92,20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
            </svg>
            Google Play
          </a>
          <a class="gym-footer-appstore-btn"
             href="https://apps.apple.com/gr/app/cloudtec-gym/id6757802948"
             target="_blank" rel="noopener">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            App Store
          </a>
        </div>

        <div class="gym-footer-col">
          <div class="gym-footer-col-title">Πλατφόρμα</div>
          <router-link :to="{ path: '/products/cloudtec-gym', hash: '#features' }">Χαρακτηριστικά</router-link>
          <router-link to="/products/cloudtec-gym/how-it-works">Πώς Λειτουργεί</router-link>
          <router-link :to="{ path: '/products/cloudtec-gym', hash: '#pricing' }">Τιμολόγηση</router-link>
          <router-link :to="{ path: '/products/cloudtec-gym', hash: '#faq' }">FAQ</router-link>
        </div>

        <div class="gym-footer-col">
          <div class="gym-footer-col-title">Επικοινωνία</div>
          <a href="mailto:info@cloudtec.gr">info@cloudtec.gr</a>
          <a href="tel:+306985786121">+30 698 578 6121</a>
          <router-link to="/contact">Φόρμα επικοινωνίας</router-link>
        </div>

        <div class="gym-footer-col">
          <div class="gym-footer-col-title">Νομικά</div>
          <router-link to="/products/cloudtec-gym/terms">Όροι Χρήσης</router-link>
          <router-link to="/products/cloudtec-gym/privacy">Πολιτική Απορρήτου</router-link>
        </div>
      </div>

      <div class="gym-footer-bottom">
        <p>&copy; {{ currentYear }} Cloudtec Gym. Powered by <router-link to="/">CloudTec</router-link>.</p>
        <a class="gym-footer-top" href="#top">Πάνω ↑</a>
      </div>
    </footer>

    <!-- ══════════════════════════════════════════
         LIGHTBOX
    ══════════════════════════════════════════ -->
    <Teleport to="body">
      <transition name="lb-fade">
        <div v-if="lightbox.open" class="lb-overlay" @click.self="closeLightbox">
          <button class="lb-close" @click="closeLightbox" aria-label="Κλείσιμο">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </button>

          <button
            v-if="lightbox.images.length > 1"
            class="lb-nav lb-nav--prev"
            @click="lightboxPrev"
            aria-label="Προηγούμενο"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
          </button>

          <div class="lb-img-wrap">
            <transition name="lb-img" mode="out-in">
              <img
                :key="lightbox.index"
                :src="lightbox.images[lightbox.index].src"
                :alt="lightbox.images[lightbox.index].alt"
              />
            </transition>
            <div class="lb-caption">{{ lightbox.images[lightbox.index].alt }}</div>
            <div v-if="lightbox.images.length > 1" class="lb-dots">
              <button
                v-for="(_, di) in lightbox.images"
                :key="di"
                :class="['lb-dot', { 'lb-dot--active': lightbox.index === di }]"
                @click="lightbox.index = di"
              ></button>
            </div>
          </div>

          <button
            v-if="lightbox.images.length > 1"
            class="lb-nav lb-nav--next"
            @click="lightboxNext"
            aria-label="Επόμενο"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
          </button>
        </div>
      </transition>
    </Teleport>

  </div>
</template>

<script>
import GymNavBar from '@/components/Products/CloudtecGym/GymNavBar.vue';
import dashWidgetsImg     from '@/assets/CloudtecGym/Dashboard_widgets.png';
import dashCalendarImg    from '@/assets/CloudtecGym/Dashboard_calendar.png';
import membersListImg     from '@/assets/CloudtecGym/Members_list.png';
import memberDetailsImg   from '@/assets/CloudtecGym/Member_details.png';
import classesImg         from '@/assets/CloudtecGym/Classes.png';
import bookingsImg        from '@/assets/CloudtecGym/Bookings.png';
import programBookingsImg from '@/assets/CloudtecGym/Program_Bookings_Drag&Drop.png';
import qrCheckinImg       from '@/assets/CloudtecGym/QR_Checkin.png';
import workoutImg         from '@/assets/CloudtecGym/Workout_templates.png';
import questionnaireImg   from '@/assets/CloudtecGym/questionaire.png';
import membershipPlansImg from '@/assets/CloudtecGym/Membership_plans.png';
import financesImg        from '@/assets/CloudtecGym/Finances.png';
import customThemeImg     from '@/assets/CloudtecGym/Custom_theme&Logo.png';
import openingHoursImg    from '@/assets/CloudtecGym/Opening_Hours.png';
import lightThemeImg      from '@/assets/CloudtecGym/Light_Theme.png';
import categoriesImg      from '@/assets/CloudtecGym/Categories.png';
import coachesImg         from '@/assets/CloudtecGym/Coaches.png';
import programmImg         from '@/assets/CloudtecGym/Programm_Sessions.png';
import membershipsImg         from '@/assets/CloudtecGym/Memberships.png';

import dashAppImg      from '@/assets/phone/AppStore.Dashboard.png';
import bookingsAppImg  from '@/assets/phone/AppStore.Bookings.png';
import trackingAppImg  from '@/assets/phone/AppStore.SessionsTracking.png';
import sessionsAppImg  from '@/assets/phone/AppStore.SessionsAnalytics.png';
import profileAppImg   from '@/assets/phone/AppStore.ProfileAnalytics.png';

export default {
  name: 'GymHowItWorks',
  components: { GymNavBar },

  data() {
    return {
      currentYear: new Date().getFullYear(),

      sliderIndexes: {
        dashboard: 0,
        members:   0,
        classes:   0,
        checkin:   0,
        workouts:  0,
        finances:  0,
        settings:  0,
      },

      lightbox: {
        open:   false,
        images: [],
        index:  0,
      },

      adminFeatures: [
        {
          id: 'dashboard',
          title: 'Dashboard & Επισκόπηση',
          desc: 'Έχεις πλήρη εικόνα της κατάστασης του γυμναστηρίου σου σε πραγματικό χρόνο. Στατιστικά, έσοδα, ενεργά μέλη, επερχόμενες κρατήσεις και ημερολόγιο — όλα στην αρχική σελίδα.',
          points: [
            'KPIs & στατιστικά σε πραγματικό χρόνο',
            'Ημερολόγιο τμημάτων & κρατήσεων',
            'Επισκόπηση εσόδων & πληρωμών',
            'Γρήγορες ενέργειες από την αρχική',
          ],
          images: [
            { src: dashWidgetsImg,  alt: 'Dashboard — Widgets & Στατιστικά' },
            { src: dashCalendarImg, alt: 'Dashboard — Ημερολόγιο' },
          ],
        },
        {
          id: 'members',
          title: 'Διαχείριση Μελών',
          desc: 'Πλήρης εικόνα κάθε μέλους: στοιχεία επικοινωνίας, ιστορικό παρουσιών, ενεργές συνδρομές, workout ιστορικό και προσωπικές σημειώσεις. Αναζήτηση και φιλτράρισμα μελών με ένα κλικ.',
          points: [
            'Λίστα & γρήγορη αναζήτηση μελών',
            'Ιστορικό παρουσιών & check-ins',
            'Ενεργές & παλιές συνδρομές ανά μέλος',
            'Προσωπικές σημειώσεις & workout ιστορικό',
          ],
          images: [
            { src: membersListImg,   alt: 'Λίστα Μελών' },
            { src: memberDetailsImg, alt: 'Στοιχεία Μέλους' },
          ],
        },
        {
          id: 'classes',
          title: 'Τμήματα, Πρόγραμμα & Κρατήσεις',
          desc: 'Δημιούργησε τμήματα με ωράριο, χωρητικότητα και προπονητή. Το drag & drop scheduler σου επιτρέπει να οργανώσεις το εβδομαδιαίο πρόγραμμα σε λίγα δευτερόλεπτα. Διαχειρίσου κρατήσεις και λίστα αναμονής.',
          points: [
            'Drag & Drop εβδομαδιαίο scheduler',
            'Ορισμός χωρητικότητας ανά τμήμα',
            'Αυτόματη λίστα αναμονής',
            'Πλήρες ιστορικό κρατήσεων',
          ],
          images: [
            { src: classesImg,         alt: 'Τμήματα' },
            { src: programmImg,         alt: 'Προγράμματα' },
            { src: programBookingsImg, alt: 'Drag & Drop Scheduler' },
            { src: bookingsImg,        alt: 'Λίστα Κρατήσεων' },
          ],
        },
        {
          id: 'checkin',
          title: 'QR Check-in',
          desc: 'Το μέλος σκανάρει το QR code του από το mobile app και το check-in γίνεται αυτόματα. Χωρίς χαρτιά, χωρίς αναμονή — ο πιο γρήγορος τρόπος ελέγχου παρουσίας.',
          points: [
            'Instant check-in με QR code από το app',
            'Αυτόματη καταγραφή παρουσίας',
            'Αποτροπή διπλού check-in',
            'Πλήρες ιστορικό παρουσιών',
          ],
          images: [
            { src: qrCheckinImg, alt: 'QR Check-in' },
          ],
        },
        {
          id: 'workouts',
          title: 'Workout Templates & Ερωτηματολόγια',
          desc: 'Σχεδίασε workout templates και ανάθεσέ τα στα μέλη σου. Δημιούργησε ερωτηματολόγια για αξιολόγηση στόχων, intake forms και παρακολούθηση της προόδου υγείας.',
          points: [
            'Custom workout builder με ασκήσεις & sets',
            'Ανάθεση προγραμμάτων ανά μέλος',
            'Intake forms & health questionnaires',
            'Παρακολούθηση προόδου μελών',
          ],
          images: [
            { src: workoutImg,       alt: 'Workout Templates' },
            { src: questionnaireImg, alt: 'Ερωτηματολόγιο' },
          ],
        },
        {
          id: 'finances',
          title: 'Συνδρομές & Οικονομικά',
          desc: 'Ορίσε ευέλικτα πλάνα συνδρομών και διαχειρίσου τις ενεργές συνδρομές κάθε μέλους. Πλήρεις οικονομικές αναφορές με δυνατότητα export σε Excel ή PDF.',
          points: [
            'Ευέλικτα πλάνα & τιμολόγηση',
            'Διαχείριση ενεργών συνδρομών',
            'Αναφορές εσόδων & πληρωμών',
            'Export σε Excel / PDF',
          ],
          images: [
            { src: membershipPlansImg, alt: 'Πλάνα Συνδρομών' },
            { src: membershipsImg,     alt: 'Συνδρομές' },
            { src: financesImg,        alt: 'Οικονομικές Αναφορές' },
          ],
        },
        {
          id: 'settings',
          title: 'Ρυθμίσεις & Custom Branding',
          desc: 'Πλήρης προσαρμογή: ορίσε ώρες λειτουργίας, πρόσθεσε προπονητές, δημιούργησε κατηγορίες τμημάτων και φόρεσε στο mobile app τα χρώματα και το logo του γυμναστηρίου σου.',
          points: [
            'Custom theme & logo στο mobile app',
            'Ώρες λειτουργίας & εξαιρέσεις',
            'Διαχείριση προπονητών & κατηγοριών',
            'Light & Dark mode υποστήριξη',
          ],
          images: [
            { src: lightThemeImg,   alt: 'Light Theme' },
            { src: customThemeImg,  alt: 'Custom Theme & Logo' },
            { src: openingHoursImg, alt: 'Ώρες Λειτουργίας' },
            { src: coachesImg,      alt: 'Διαχείριση Προπονητών' },
            { src: categoriesImg,   alt: 'Κατηγορίες Τμημάτων' },
          ],
        },
      ],

      memberScreens: [
        {
          img:   dashAppImg,
          title: 'Αρχική & Dashboard',
          desc:  'Επόμενες κρατήσεις, στατιστικά εβδομάδας και νέα του γυμναστηρίου με μία ματιά.',
        },
        {
          img:   bookingsAppImg,
          title: 'Κρατήσεις',
          desc:  'Εύκολη κράτηση θέσης σε τμήμα με 2 taps. Push notification επιβεβαίωσης αμέσως.',
        },
        {
          img:   trackingAppImg,
          title: 'Παρακολούθηση Προπόνησης',
          desc:  'Workout templates που έχουν ανατεθεί στο μέλος, με καταγραφή προόδου ανά άσκηση.',
        },
        {
          img:   sessionsAppImg,
          title: 'Αναλυτικά Sessions',
          desc:  'Στατιστικά ανά session: διάρκεια, ασκήσεις, ένταση. Γραφήματα προόδου ανά εβδομάδα.',
        },
        {
          img:   profileAppImg,
          title: 'Προφίλ & Αναλυτικά',
          desc:  'Streak, συνολικές ώρες προπόνησης, ιστορικό παρουσιών και ενεργή συνδρομή.',
        },
      ],
    };
  },

  computed: {
    memberScreenImages() {
      return this.memberScreens.map(s => ({ src: s.img, alt: s.title }));
    },
  },

  methods: {
    prevSlide(id, count) {
      this.sliderIndexes[id] = (this.sliderIndexes[id] - 1 + count) % count;
    },
    nextSlide(id, count) {
      this.sliderIndexes[id] = (this.sliderIndexes[id] + 1) % count;
    },
    openLightbox(images, index) {
      this.lightbox = { open: true, images, index };
      document.body.style.overflow = 'hidden';
    },
    closeLightbox() {
      this.lightbox.open = false;
      document.body.style.overflow = '';
    },
    lightboxPrev() {
      const len = this.lightbox.images.length;
      this.lightbox.index = (this.lightbox.index - 1 + len) % len;
    },
    lightboxNext() {
      const len = this.lightbox.images.length;
      this.lightbox.index = (this.lightbox.index + 1) % len;
    },
  },

  mounted() {
    this._onKeyDown = (e) => {
      if (!this.lightbox.open) return;
      if (e.key === 'Escape')      this.closeLightbox();
      if (e.key === 'ArrowLeft')   this.lightboxPrev();
      if (e.key === 'ArrowRight')  this.lightboxNext();
    };
    window.addEventListener('keydown', this._onKeyDown);
  },

  beforeUnmount() {
    if (this._onKeyDown) window.removeEventListener('keydown', this._onKeyDown);
    document.body.style.overflow = '';
  },
};
</script>

<style scoped>
/* ── Base ── */
.gym-page {
  background: #253649;
  color: #fff;
  font-family: inherit;
  overflow-x: hidden;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  flex-direction: column;
}

.muted  { color: rgba(255,255,255,0.72); }
.accent { color: #FFC947; }

.label-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border: 1px solid rgba(255,201,71,0.35);
  background: rgba(255,201,71,0.08);
  color: #FFC947;
  border-radius: 999px;
  padding: 0.3rem 0.9rem;
  margin-bottom: 0.6rem;
}

/* ── PAGE HERO ── */
.hiw-hero {
  padding: clamp(6rem, 10vw, 9rem) 1.5rem clamp(3.5rem, 5vw, 5rem);
  text-align: center;
}

.hiw-hero-inner {
  max-width: 680px;
  margin: 0 auto;
}

.hiw-hero-inner h1 {
  font-size: clamp(2.4rem, 4vw, 3.6rem);
  font-weight: 900;
  line-height: 1.15;
  margin: 0.5rem 0 1rem;
  overflow-wrap: break-word;
  word-break: break-word;
}

.hiw-hero-inner p {
  font-size: 1.1rem;
  line-height: 1.75;
  margin: 0 0 2rem;
}

.hiw-hero-inner strong { color: #fff; }

.hiw-pills {
  display: flex;
  gap: 0.9rem;
  justify-content: center;
  flex-wrap: wrap;
}

.hiw-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.75rem 1.8rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 700;
  background: #FFC947;
  color: #1a2b3c;
  text-decoration: none;
  transition: opacity 0.2s, transform 0.2s;
}

.hiw-pill:hover { opacity: 0.88; transform: translateY(-1px); }

.hiw-pill--outline {
  background: transparent;
  color: #FFC947;
  border: 2px solid rgba(255,201,71,0.45);
}

.hiw-pill--outline:hover { background: rgba(255,201,71,0.07); }

/* ── PANEL HEADER ── */
.panel-hdr {
  text-align: center;
  padding: clamp(4rem, 6vw, 6rem) 1.5rem 3rem;
}

.panel-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  padding: 0.35rem 1rem;
  border-radius: 999px;
  border: 1px solid rgba(255,201,71,0.3);
  background: rgba(255,201,71,0.08);
  color: #FFC947;
  margin-bottom: 1rem;
}

.panel-tag--member {
  border-color: rgba(99,179,237,0.35);
  background: rgba(99,179,237,0.08);
  color: #63b3ed;
}

.panel-hdr h2 {
  font-size: clamp(1.9rem, 3vw, 2.8rem);
  font-weight: 900;
  margin: 0 0 0.8rem;
  overflow-wrap: break-word;
  word-break: break-word;
}

.panel-hdr > p {
  font-size: 1.05rem;
  line-height: 1.7;
  max-width: 620px;
  margin: 0 auto 1.5rem;
}

/* ── FEATURE ROWS ── */
.feat-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(2rem, 4vw, 5rem);
  align-items: center;
  padding: clamp(3rem, 5vw, 5rem) clamp(1.5rem, 4vw, 4rem);
  max-width: 1200px;
  margin: 0 auto;
  border-top: 1px solid rgba(255,255,255,0.07);
}

.feat-row--rev .feat-content { order: 2; }
.feat-row--rev .feat-imgs,
.feat-row--rev .img-slider   { order: 1; }

.feat-num {
  font-size: 4rem;
  font-weight: 900;
  color: rgba(255,201,71,0.12);
  line-height: 1;
  margin-bottom: 0.4rem;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
}

.feat-content h3 {
  font-size: clamp(1.3rem, 2vw, 1.7rem);
  font-weight: 800;
  margin: 0 0 0.75rem;
  line-height: 1.3;
  overflow-wrap: break-word;
  word-break: break-word;
}

.feat-content > p {
  font-size: 1rem;
  line-height: 1.75;
  margin: 0 0 1.5rem;
}

.feat-points {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.feat-points li {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.95rem;
  color: rgba(255,255,255,0.88);
}

.pt-check {
  color: #FFC947;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  margin-top: 0.05rem;
}

/* ── SINGLE IMAGE ── */
.feat-imgs {
  position: relative;
}

.screen-wrap {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.45);
  border: 1px solid rgba(255,255,255,0.08);
  background: #1e2e3d;
  cursor: zoom-in;
  transition: transform 0.2s, box-shadow 0.2s;
}

.screen-wrap:hover { transform: translateY(-3px); box-shadow: 0 16px 48px rgba(0,0,0,0.55); }

.screen-wrap img { width: 100%; height: auto; display: block; }

.zoom-hint {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0,0,0,0.55);
  border-radius: 6px;
  padding: 5px;
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s;
  color: #fff;
  pointer-events: none;
}

.screen-wrap:hover .zoom-hint { opacity: 1; }

/* ── IMAGE SLIDER ── */
.img-slider {
  position: relative;
}

.slider-img-wrap {
  position: relative;
}

.slider-img-wrap img {
  width: 100%;
  height: auto;
  display: block;
}

.slider-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  background: rgba(0,0,0,0.55);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, transform 0.15s;
  backdrop-filter: blur(4px);
}

.slider-btn:hover { background: rgba(255,201,71,0.85); color: #1a2535; transform: translateY(-50%) scale(1.08); }

.slider-btn--prev { left: 10px; }
.slider-btn--next { right: 10px; }

.slider-dots {
  display: flex;
  gap: 6px;
  justify-content: center;
  margin-top: 10px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.25);
  cursor: pointer;
  padding: 0;
  transition: background 0.2s, transform 0.2s;
}

.dot--active { background: #FFC947; transform: scale(1.3); }
.dot:hover:not(.dot--active) { background: rgba(255,255,255,0.5); }

.slider-counter {
  text-align: center;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.4);
  margin-top: 4px;
}

/* Slider image fade transition */
.fade-enter-active, .fade-leave-active { transition: opacity 0.22s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* ── MEMBER APP SECTION ── */
.member-section {
  background: rgba(255,255,255,0.025);
  padding-bottom: 4rem;
}

.store-badges {
  display: flex;
  gap: 0.9rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 1.5rem;
}

.store-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.4rem;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.06);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s;
}

.store-btn:hover { background: rgba(255,255,255,0.12); }
.store-btn svg { width: 18px; height: 18px; flex-shrink: 0; }

.member-screens {
  display: flex;
  gap: 1.5rem;
  overflow-x: auto;
  padding: 2rem max(2rem, calc(50vw - 600px)) 3rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(255,201,71,0.3) transparent;
  justify-content: center;
}

.member-screen {
  flex: 0 0 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  cursor: zoom-in;
}

.phone-frame {
  position: relative;
  width: 200px;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(0,0,0,0.5);
  border: 1px solid rgba(255,255,255,0.1);
  transition: transform 0.22s, box-shadow 0.22s;
}

.member-screen:hover .phone-frame {
  transform: translateY(-6px);
  box-shadow: 0 24px 60px rgba(0,0,0,0.6);
}

.phone-frame img { width: 100%; height: auto; display: block; }

.phone-zoom-hint {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0,0,0,0.55);
  border-radius: 6px;
  padding: 5px;
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s;
  color: #fff;
  pointer-events: none;
}

.member-screen:hover .phone-zoom-hint { opacity: 1; }

.screen-label { text-align: center; }

.screen-title { font-size: 0.9rem; font-weight: 700; margin-bottom: 0.35rem; }
.screen-desc  { font-size: 0.78rem; line-height: 1.55; }

/* ── CTA ── */
.hiw-cta {
  padding: clamp(4rem, 6vw, 6rem) 1.5rem;
  text-align: center;
  border-top: 1px solid rgba(255,255,255,0.08);
}

.hiw-cta h2 {
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 900;
  margin: 0 0 0.75rem;
}

.hiw-cta p { font-size: 1.05rem; margin: 0 0 2rem; }

.cta-row {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 2.2rem;
  border-radius: 999px;
  background: #FFC947;
  color: #1a2b3c;
  font-weight: 800;
  font-size: 1rem;
  text-decoration: none;
  transition: opacity 0.2s, transform 0.2s;
}

.cta-primary:hover { opacity: 0.88; transform: translateY(-1px); }

.cta-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 2.2rem;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.2);
  color: rgba(255,255,255,0.9);
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  transition: background 0.2s;
}

.cta-secondary:hover { background: rgba(255,255,255,0.06); }

/* ── LIGHTBOX ── */
.lb-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(5, 12, 20, 0.96);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.lb-close {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 10001;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.08);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.lb-close:hover { background: rgba(255,255,255,0.18); }

.lb-nav {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10001;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.18);
  background: rgba(20, 30, 44, 0.7);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, transform 0.15s;
  backdrop-filter: blur(4px);
}

.lb-nav--prev { left: 1rem; }
.lb-nav--next { right: 1rem; }
.lb-nav:hover { background: rgba(255,201,71,0.85); color: #1a2535; }
.lb-nav--prev:hover { transform: translateY(-50%) scale(1.08); }
.lb-nav--next:hover { transform: translateY(-50%) scale(1.08); }

.lb-img-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3.5rem 5rem;
  box-sizing: border-box;
}

.lb-img-wrap img {
  max-width: 100%;
  max-height: calc(100vh - 8rem);
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 24px 80px rgba(0,0,0,0.7);
  display: block;
}

.lb-caption {
  font-size: 0.85rem;
  color: rgba(255,255,255,0.5);
  text-align: center;
}

.lb-dots {
  display: flex;
  gap: 7px;
  justify-content: center;
}

.lb-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.25);
  cursor: pointer;
  padding: 0;
  transition: background 0.2s, transform 0.2s;
}

.lb-dot--active { background: #FFC947; transform: scale(1.35); }
.lb-dot:hover:not(.lb-dot--active) { background: rgba(255,255,255,0.5); }

/* ── FOOTER ── */
.gym-footer {
  background: #0f1922;
  color: #fff;
  padding: clamp(3rem, 5vw, 4rem) 1.5rem 0;
}

.gym-footer-main {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.6fr 1fr 1fr 1fr;
  gap: 2.5rem 2rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid rgba(255,255,255,0.07);
}

.gym-footer-logo {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 0.9rem;
}

.gym-logo-img{
  width: 38px;
  height: 38px;
}

.gym-footer-logo strong { color: #FFC947; }

.gym-footer-tagline {
  font-size: 0.85rem;
  line-height: 1.6;
  color: rgba(255,255,255,0.55);
  margin: 0 0 1.2rem;
  max-width: 260px;
}

.gym-footer-appstore-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 800;
  color: #1a2535;
  background: #FFC947;
  border-radius: 999px;
  padding: 0.55rem 1.1rem;
  transition: opacity 0.15s;
  margin-bottom: 0.5rem;
  margin-right: 0.6rem;
}

.gym-footer-appstore-btn:hover { opacity: 0.88; }

.gym-footer-col-title {
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.4);
  margin-bottom: 1rem;
}

.gym-footer-col {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.gym-footer-col a,
.gym-footer-col .router-link-active {
  text-decoration: none;
  color: rgba(255,255,255,0.6);
  font-size: 0.88rem;
  transition: color 0.15s;
}

.gym-footer-col a:hover { color: #FFC947; }

.gym-footer-bottom {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.2rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.8rem;
  color: rgba(255,255,255,0.35);
}

.gym-footer-bottom a,
.gym-footer-bottom .router-link-active {
  color: rgba(255,255,255,0.5);
  text-decoration: none;
  transition: color 0.15s;
}

.gym-footer-bottom a:hover { color: #FFC947; }

.gym-footer-top { font-weight: 700; font-size: 0.82rem; }

@media (max-width: 860px) {
  .gym-footer-main { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 540px) {
  .gym-footer-main { grid-template-columns: 1fr; }
  .gym-footer-bottom { flex-direction: column; text-align: center; }
}

/* Lightbox transitions */
.lb-fade-enter-active, .lb-fade-leave-active { transition: opacity 0.25s ease; }
.lb-fade-enter-from,   .lb-fade-leave-to     { opacity: 0; }

.lb-img-enter-active, .lb-img-leave-active { transition: opacity 0.18s ease; }
.lb-img-enter-from,   .lb-img-leave-to     { opacity: 0; }

/* ── RESPONSIVE ── */
@media (max-width: 900px) {
  .feat-row {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: clamp(2.5rem, 4vw, 3.5rem) 1.25rem;
  }

  .feat-row--rev .feat-content { order: 0; }
  .feat-row--rev .feat-imgs,
  .feat-row--rev .img-slider   { order: 0; }
}

@media (max-width: 560px) {
  .member-screens {
    justify-content: flex-start;
    padding: 2rem 1.25rem 3rem;
  }

  .member-screen { flex: 0 0 160px; }
  .phone-frame   { width: 160px; }

  .lb-nav { width: 38px; height: 38px; }
  .lb-nav--prev { left: 0.25rem; }
  .lb-nav--next { right: 0.25rem; }
  .lb-img-wrap { padding: 3.5rem 1rem; }
}
</style>
